import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap, shareReplay, tap } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { DbKeyService } from '../core/security/db-key.service';
import { AuthResource } from './resources/auth.resource';
import { BackgroundDataLoadService } from './background-data-load.service';
import { SecurityStorage } from './storages/security.storage';
import { AuthRequestModel } from '../models/auth-request.model';
import { UserModel } from '../models/user.model';
import { SyncStorage } from './storages/sync.storage';
import { ThumediDatabaseHelper } from '../core/abstractions/thumedi-database.helper';
import { ManagingDataStorage } from './storages/managing-data.storage';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private router = inject(Router);
    private securityStorage = inject(SecurityStorage);
    private syncStorage = inject(SyncStorage);
    private authResource = inject(AuthResource);
    private databaseHelper = inject(ThumediDatabaseHelper);
    private ManagingDataStorage = inject(ManagingDataStorage);
    private dbKeyService = inject(DbKeyService);
    private backgroundDataLoadService = inject(BackgroundDataLoadService);

    redirectUrl?: string;

    /**
     * Shared in-flight refresh request (Issue #6). While a refresh is running,
     * every concurrent 401 subscribes to this same observable so a burst of
     * unauthenticated responses triggers exactly one refresh HTTP call.
     */
    private refreshInFlight$?: Observable<UserModel>;

    login(data: AuthRequestModel): Observable<void> {
        return this.authResource.login(data).pipe(
            mergeMap(response => {
                const currentUser = this.securityStorage.getUserInfo();

                if (!!currentUser && currentUser.id != response.id) {
                    return from(this.clearAllUserRelatedData()).pipe(
                        map(() => response)
                    );
                } else {
                    return of(response);
                }
            }),
            // The key is intentionally NOT part of UserModel, so saveUserInfo
            // never persists it to localStorage.
            map(response => this.securityStorage.saveUserInfo(response)),
            mergeMap(() => this.fetchDbKeyIfEnabled())
        );
    }

    logout(): void {
        // Capture the refresh token before the session is wiped so it can be
        // invalidated server-side (Issue #6). Revoke is best-effort: it must
        // never block or fail the logout (e.g. offline).
        const refreshToken = this.securityStorage.getUserInfo()?.refreshToken;

        this.revokeRefreshToken(refreshToken)
            .pipe(
                mergeMap(() => this.clearAllUserRelatedData()),
                tap(() => this.router.navigate(['login']))
            )
            .subscribe();
    }

    isLoggedIn(): boolean {
        const userInfo = this.securityStorage.getUserInfo();

        if (!userInfo) {
            return false;
        }

        const accessTokenValid = !new JwtHelperService().isTokenExpired(userInfo.token);

        // Treat the user as still logged in while the access token is expired
        // but a refresh token is available: the interceptor can obtain a fresh
        // access token silently before a guard would bounce the user to /login
        // (Issue #6). Without a refresh token the previous behaviour — logged in
        // only while the access token is valid — is preserved exactly.
        return accessTokenValid || !!userInfo.refreshToken;
    }

    /**
     * Returns the current access token, or `undefined` when no user is stored.
     * Single source of truth for both the HTTP interceptor and the SignalR
     * `accessTokenFactory` (Issue #6), so token rotation is picked up everywhere.
     */
    getToken(): string | undefined {
        return this.securityStorage.getUserInfo()?.token;
    }

    /**
     * Exchanges the stored refresh token for a fresh access token (Issue #6).
     *
     * Concurrent callers share a single in-flight request. The refreshed
     * `UserModel` (new access + rotated refresh token) is persisted before it is
     * emitted, so the retried request and the next SignalR (re)connect both pick
     * up the new token. When no refresh token is stored the flow degrades to the
     * previous behaviour by erroring without any HTTP call, letting the caller
     * fall through to the existing logout/redirect path.
     */
    refreshSession(): Observable<UserModel> {
        if (this.refreshInFlight$) {
            return this.refreshInFlight$;
        }

        const refreshToken = this.securityStorage.getUserInfo()?.refreshToken;

        if (!refreshToken) {
            return throwError(() => new Error('No refresh token available'));
        }

        this.refreshInFlight$ = this.authResource.refresh(refreshToken).pipe(
            tap(updated => this.securityStorage.saveUserInfo(updated)),
            finalize(() => (this.refreshInFlight$ = undefined)),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        return this.refreshInFlight$;
    }

    private revokeRefreshToken(refreshToken?: string): Observable<void> {
        if (!refreshToken) {
            return of(undefined);
        }

        return this.authResource.revoke(refreshToken).pipe(
            catchError(() => of(undefined))
        );
    }

    /**
     * Ensures an IndexedDB encryption key is available before the local
     * database is opened (Issue #4). Used on app bootstrap when a valid JWT
     * already exists (page reload / service-worker update reload).
     *
     * Resolves to `true` when a key is available (or encryption is disabled),
     * and `false` when the key could not be obtained (e.g. offline with no
     * cached key) — in which case the caller must keep the local DB closed and
     * require an online sign-in.
     */
    ensureDbKey(): Observable<boolean> {
        if (!environment.encryptIndexedDb) {
            return of(true);
        }

        if (this.dbKeyService.hasKey()) {
            return of(true);
        }

        if (!this.isLoggedIn()) {
            return of(false);
        }

        return this.authResource.getDbKey().pipe(
            tap(key => this.dbKeyService.setKey(key)),
            map(() => true),
            catchError(() => of(false))
        );
    }

    private fetchDbKeyIfEnabled(): Observable<void> {
        if (!environment.encryptIndexedDb) {
            return of(undefined);
        }

        return this.authResource.getDbKey().pipe(
            tap(key => this.dbKeyService.setKey(key)),
            map(() => undefined)
        );
    }

    private clearAllUserRelatedData() {
        return forkJoin([
            from(this.databaseHelper.getTable('bookings').clear()),
            from(this.databaseHelper.getTable('customers').clear()),
            from(this.databaseHelper.getTable('allocations').clear()),
            from(this.databaseHelper.getTable('providers').clear()),
            from(this.databaseHelper.getTable('todos').clear()),
            from(this.databaseHelper.getTable('unsyncedChanges').clear()),
            from(this.databaseHelper.getTable('templates').clear()),
            from(this.databaseHelper.getTable('tableStates').clear())
        ]).pipe(
            tap(() => {
                // Stop background loading first: cancels in-flight token-bearing
                // requests and terminates the workers so nothing can complete
                // after the session is torn down (Issue #5).
                this.backgroundDataLoadService.stop();
                this.securityStorage.eraseUserInfo();
                this.syncStorage.eraseLastFullSync();
                this.syncStorage.eraseLastSync();
                this.ManagingDataStorage.eraseData();
                // Wipe the in-memory encryption key on logout / user change.
                this.dbKeyService.clear();
            })
        );
    }
}
